/**
 * BrambleRam.ts
 * Armoured charger miniboss for World 3 (Brasscloud Foundry).
 *
 * Behaviour: stands rooted until Nova enters detection range, then
 * telegraphs a charge, dashes forward at high speed, and slams into a
 * wall on miss — exposing its vulnerable back for a fixed stun window.
 * Shards and stomps that connect during STUNNED deduct one HP from a
 * pool of 3. Outside the stun window the Ram is fully armoured —
 * every shard reads as `BLOCKED` and every stomp is rejected.
 *
 * State machine: IDLE → TELEGRAPH → CHARGING → STUNNED → IDLE
 *                 (with HP=0 at any STUNNED hit transitioning to DEAD)
 *
 * Rules (AGENTS.md):
 *   - No React imports. No async. No Skia imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 3 miniboss greybox stub. Wired into levels when W3 ships.
 * See ROADMAP.md Phase 4 (`BrambleRam.ts`).
 *
 * @module BrambleRam
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import { resolveAllPlatforms } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type BrambleRamState = 'IDLE' | 'TELEGRAPH' | 'CHARGING' | 'STUNNED' | 'DEAD';

/**
 * Projectile-impact result. `BLOCKED` is the headline value here — the
 * Ram is armoured outside the STUNNED window and rejects every shard
 * from the front, top, or sides. `HURT` only fires while STUNNED.
 */
export type BrambleRamHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/** Width/height of the Ram hitbox in logical pixels — larger than a regular mob. */
export const BRAMBLE_RAM_WIDTH = 36;
export const BRAMBLE_RAM_HEIGHT = 32;
/** Greybox render colour — bramble brown. */
export const BRAMBLE_RAM_COLOR = '#5a3a1f';

/** Greybox HP pool. Three connected stun-window hits to kill. */
export const BRAMBLE_RAM_MAX_HP = 3;

/** Horizontal range at which the Ram acquires Nova and begins the charge sequence. */
const DETECTION_RANGE_X = 400;
/** Vertical range — Nova must be roughly on the same ground tier. */
const DETECTION_RANGE_Y = BRAMBLE_RAM_HEIGHT;

/** Wind-up before the charge. Long enough that the player can react. */
const TELEGRAPH_DURATION = 0.5;
/** Forward speed while CHARGING — fast enough to feel dangerous but readable. */
const CHARGE_SPEED = 240;
/** Time the Ram is stunned (and vulnerable) after a missed charge. */
const STUN_DURATION = 1.5;

// DECISION: Reusable hitbox object to avoid per-tick allocations in the
// engine hot path (mirrors Gearling / SparkImp / Shellback).
const _hitbox = { x: 0, y: 0, width: BRAMBLE_RAM_WIDTH, height: BRAMBLE_RAM_HEIGHT };

/**
 * BrambleRam — World 3 miniboss (Brasscloud Foundry).
 *
 * Spawn with `new BrambleRam(x, y)`. Call `update(dt, level, nova?)` each
 * fixed tick; pass the latest Nova snapshot so the Ram can detect range
 * and lock its facing direction at telegraph time. Read `dead` to know
 * when to remove from the scene; read `hp` for HUD purposes.
 */
export class BrambleRam extends BaseEntity {
  public readonly entityType = 'BrambleRam';
  public readonly color = BRAMBLE_RAM_COLOR;
  public readonly maxHp = BRAMBLE_RAM_MAX_HP;
  public state: BrambleRamState = 'IDLE';

  /**
   * True once dead. BrambleRam has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  /** Remaining HP. Decremented only by hits landed during the STUNNED window. */
  public hp = BRAMBLE_RAM_MAX_HP;
  /** True when facing right (used by renderer for sprite flip + charge direction). */
  public facingRight = true;
  private _telegraphTimer = 0;
  private _stunTimer = 0;

  constructor(x: number, y: number) {
    super(x, y, BRAMBLE_RAM_WIDTH, BRAMBLE_RAM_HEIGHT);
  }

  /**
   * Advance the Ram by one fixed tick.
   * @param dt    Fixed-step delta in seconds.
   * @param level Current level geometry — read-only.
   * @param nova  Optional Nova runtime snapshot. Without one the Ram stays IDLE.
   */
  public update(dt: number, level: LevelRuntime, nova?: NovaRuntime): void {
    if (this.state === 'DEAD') return;

    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    switch (this.state) {
      case 'IDLE':
        this.vx = 0;
        this._integrateAndResolve(dt, level);
        if (nova && this._novaInRange(nova)) {
          this._faceNova(nova);
          this._enterTelegraph();
        }
        break;
      case 'TELEGRAPH':
        this.vx = 0;
        this._integrateAndResolve(dt, level);
        this._telegraphTimer -= dt;
        if (this._telegraphTimer <= 0) this._enterCharging();
        break;
      case 'CHARGING': {
        const hitWall = this._integrateAndResolve(dt, level);
        if (hitWall) this._enterStunned();
        break;
      }
      case 'STUNNED':
        this.vx = 0;
        this._integrateAndResolve(dt, level);
        this._stunTimer -= dt;
        if (this._stunTimer <= 0) {
          this.state = 'IDLE';
          Logger.debug('BrambleRam', 'Stun ended → IDLE');
        }
        break;
    }
  }

  /**
   * Integrate position + velocity, resolve platform overlaps.
   * @returns `true` if the resolver flagged a left/right wall hit — used
   *          by the CHARGING branch to trigger the STUNNED transition.
   */
  private _integrateAndResolve(dt: number, level: LevelRuntime): boolean {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const r = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = r.x;
    this.y = r.y;
    if (r.landedOnTop) this.vy = 0;
    return r.hitLeft || r.hitRight;
  }

  /**
   * Is Nova within the Ram's detection box? Horizontal distance gates the
   * charge; vertical distance keeps the Ram from chasing through floors.
   */
  private _novaInRange(nova: NovaRuntime): boolean {
    const dx = nova.x - this.x;
    const dy = nova.y - this.y;
    return Math.abs(dx) <= DETECTION_RANGE_X && Math.abs(dy) <= DETECTION_RANGE_Y;
  }

  private _faceNova(nova: NovaRuntime): void {
    this.facingRight = nova.x >= this.x;
  }

  private _enterTelegraph(): void {
    this.state = 'TELEGRAPH';
    this._telegraphTimer = TELEGRAPH_DURATION;
    Logger.debug('BrambleRam', 'Nova spotted → TELEGRAPH');
  }

  private _enterCharging(): void {
    this.state = 'CHARGING';
    this.vx = this.facingRight ? CHARGE_SPEED : -CHARGE_SPEED;
    Logger.debug('BrambleRam', 'Charge!');
  }

  private _enterStunned(): void {
    this.state = 'STUNNED';
    this._stunTimer = STUN_DURATION;
    this.vx = 0;
    Logger.debug('BrambleRam', 'Slammed wall → STUNNED');
  }

  /**
   * Apply one projectile hit.
   * @returns `'BLOCKED'` while armoured (any state except STUNNED),
   *          `'HURT'` when the hit landed during STUNNED,
   *          `'IGNORED'` once DEAD.
   */
  public applyShardHit(): BrambleRamHitResult {
    if (this.state === 'DEAD') return 'IGNORED';
    if (this.state !== 'STUNNED') return 'BLOCKED';
    this._takeDamage();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. The Ram is armoured from above outside STUNNED.
   * @returns `true` when the stomp connected (HP decremented), `false` otherwise.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD') return false;
    if (this.state !== 'STUNNED') return false;
    this._takeDamage();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts).
   * @param fromStomp `true` for stomp contact, `false`/omitted for shards.
   * @returns `true` if this hit dealt damage; `false` if blocked or ignored.
   */
  public applyHit(fromStomp = false): boolean {
    if (fromStomp) return this.applyStomp();
    return this.applyShardHit() === 'HURT';
  }

  /**
   * Deduct one HP and route to DEAD when the pool empties. Called only
   * from a successful STUNNED-state hit.
   */
  private _takeDamage(): void {
    this.hp -= 1;
    Logger.debug('BrambleRam', `Hit landed — HP ${this.hp}/${this.maxHp}`);
    if (this.hp <= 0) {
      this.state = 'DEAD';
      this.dead = true;
      Logger.debug('BrambleRam', 'Defeated');
    }
  }
}
